import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from 'components/ui/Layout';
import { useCmsPage } from 'features/cms/hooks/useCms';
import {
  LANGUAGE_OPTIONS,
  PAGE_TYPE_OPTIONS,
} from 'features/cms/types/cms.types';
import { ArrowLeft, Edit, Loader2 } from 'lucide-react';
import { formatDateTime } from 'utils/dateFormatter';

const ViewCmsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeLanguage, setActiveLanguage] = useState('english');

  // Fetch CMS page data
  const { data, isPending, isError } = useCmsPage(id);
  const cmsPage = data?.data;

  if (isPending) {
    return (
      <Layout>
        <div className='min-h-screen bg-background flex items-center justify-center'>
          <div className='flex flex-col items-center gap-4'>
            <Loader2 size={48} className='animate-spin text-primary' />
            <p className='text-muted-foreground'>Loading CMS page...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (isError || !cmsPage) {
    return (
      <Layout>
        <div className='min-h-screen bg-background flex items-center justify-center'>
          <div className='text-center'>
            <p className='text-red-400 text-lg mb-4'>Failed to load CMS page</p>
            <button
              onClick={() => navigate('/cms')}
              className='px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90'
            >
              Back to CMS
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const getPageTypeLabel = (pageType) => {
    const option = PAGE_TYPE_OPTIONS.find((opt) => opt.value === pageType);
    return option ? option.label : pageType;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published':
        return 'bg-green-900/30 text-green-400 border-green-700';
      case 'draft':
        return 'bg-yellow-900/30 text-white border-yellow-700';
      case 'archived':
        return 'bg-gray-700/30 text-muted-foreground border-gray-600';
      default:
        return 'bg-gray-700/30 text-muted-foreground border-gray-600';
    }
  };

  const Section = ({ label, children }) => (
    <div className='space-y-2'>
      <h4 className='text-sm font-medium text-muted-foreground uppercase tracking-wider'>
        {label}
      </h4>
      <div className='text-foreground'>{children}</div>
    </div>
  );

  return (
    <Layout>
      <div className='min-h-screen bg-background'>
        {/* Fixed Header */}
        <div className='sticky top-0 z-10 bg-card border-b border-border'>
          <div className='px-6 py-4'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-4'>
                <button
                  onClick={() => navigate('/cms')}
                  className='p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors'
                  title='Back to CMS list'
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <h3 className='text-lg font-semibold text-foreground'>
                    View CMS Page
                  </h3>
                  <p className='text-sm text-muted-foreground'>
                    Content page details
                  </p>
                </div>
              </div>
              <div className='flex items-center gap-3'>
                <button
                  onClick={() => navigate('/cms')}
                  className='px-4 py-2 text-sm border border-border text-foreground bg-card hover:bg-accent rounded-md transition-colors'
                >
                  Back
                </button>
                <button
                  onClick={() => navigate(`/cms/edit/${id}`)}
                  className='flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors'
                >
                  <Edit size={16} />
                  Edit Page
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className='p-6'>
          <div className='max-w-5xl mx-auto space-y-6'>
            {/* Basic Information Card */}
            <div className='bg-card border border-border rounded-lg p-6'>
              <h4 className='text-md font-semibold text-foreground mb-6'>
                Basic Information
              </h4>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                <Section label='Slug'>
                  <span className='text-sm text-primary'>
                    {cmsPage.slug}
                  </span>
                </Section>

                <Section label='Page Type'>
                  <span className='text-sm'>
                    {getPageTypeLabel(cmsPage.pageType)}
                  </span>
                </Section>

                <Section label='Status'>
                  <span
                    className={`inline-flex px-3 py-1 text-xs font-medium rounded-md border ${getStatusColor(
                      cmsPage.status,
                    )}`}
                  >
                    {cmsPage.status}
                  </span>
                </Section>

                <Section label='Published'>
                  <span className='text-sm'>
                    {cmsPage.isPublished ? 'Yes' : 'No'}
                  </span>
                </Section>

                <Section label='Created At'>
                  <span className='text-sm'>
                    {formatDateTime(cmsPage.createdAt)}
                  </span>
                </Section>

                <Section label='Last Updated'>
                  <span className='text-sm'>
                    {formatDateTime(cmsPage.updatedAt)}
                  </span>
                </Section>
              </div>

              {cmsPage.changeNote && (
                <div className='mt-6 pt-6 border-t border-border'>
                  <Section label='Change Note'>
                    <p className='text-sm'>{cmsPage.changeNote}</p>
                  </Section>
                </div>
              )}
            </div>

            {/* Language Tabs for Content */}
            <div className='bg-card border border-border rounded-lg p-6'>
              <div className='flex gap-2 mb-6 border-b border-border'>
                {LANGUAGE_OPTIONS.map((lang) => (
                  <button
                    key={lang.value}
                    type='button'
                    onClick={() => setActiveLanguage(lang.value)}
                    className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                      activeLanguage === lang.value
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>

              {/* Title */}
              <div className='mb-6'>
                <Section
                  label={`Title (${LANGUAGE_OPTIONS.find((l) => l.value === activeLanguage)?.label})`}
                >
                  <h2 className='text-2xl font-bold'>
                    {cmsPage.title?.[activeLanguage] || (
                      <span className='text-muted-foreground'>
                        Not provided
                      </span>
                    )}
                  </h2>
                </Section>
              </div>

              {/* Short Description */}
              {cmsPage.shortDescription?.[activeLanguage] && (
                <div className='mb-6'>
                  <Section
                    label={`Short Description (${LANGUAGE_OPTIONS.find((l) => l.value === activeLanguage)?.label})`}
                  >
                    <p className='text-sm text-card-foreground'>
                      {cmsPage.shortDescription[activeLanguage]}
                    </p>
                  </Section>
                </div>
              )}

              {/* Content */}
              <div>
                <Section
                  label={`Content (${LANGUAGE_OPTIONS.find((l) => l.value === activeLanguage)?.label})`}
                >
                  {cmsPage.content?.[activeLanguage] ? (
                    <div className='mt-4 bg-muted/30 border border-border rounded-lg p-6'>
                      <div
                        className='prose prose-invert max-w-none [&_h1]:text-foreground [&_h2]:text-foreground [&_h3]:text-foreground [&_h4]:text-foreground [&_h5]:text-foreground [&_h6]:text-foreground [&_p]:text-card-foreground [&_li]:text-card-foreground [&_a]:text-primary [&_strong]:text-foreground'
                        dangerouslySetInnerHTML={{
                          __html: cmsPage.content[activeLanguage],
                        }}
                      />
                    </div>
                  ) : (
                    <p className='text-sm text-muted-foreground'>
                      No content available
                    </p>
                  )}
                </Section>
              </div>
            </div>

            {/* SEO Settings Card */}
            {/* {cmsPage.seo && (
              <div className='bg-card border border-border rounded-lg p-6'>
                <h4 className='text-md font-semibold text-foreground mb-6'>
                  SEO Settings
                </h4>
                <div className='space-y-4'>
                  <Section label='Meta Title'>
                    <p className='text-sm'>{cmsPage.seo.metaTitle || '—'}</p>
                  </Section>

                  <Section label='Meta Description'>
                    <p className='text-sm'>
                      {cmsPage.seo.metaDescription || '—'}
                    </p>
                  </Section>

                  {cmsPage.seo.metaKeywords &&
                    cmsPage.seo.metaKeywords.length > 0 && (
                      <Section label='Meta Keywords'>
                        <div className='flex flex-wrap gap-2'>
                          {cmsPage.seo.metaKeywords.map((keyword, idx) => (
                            <span
                              key={idx}
                              className='px-2 py-1 text-xs bg-muted border border-border rounded-md text-foreground'
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </Section>
                    )}

                  <Section label='OG Image'>
                    <p className='text-sm'>{cmsPage.seo.ogImage || '—'}</p>
                  </Section>

                  <Section label='OG Title'>
                    <p className='text-sm'>{cmsPage.seo.ogTitle || '—'}</p>
                  </Section>

                  <Section label='OG Description'>
                    <p className='text-sm'>
                      {cmsPage.seo.ogDescription || '—'}
                    </p>
                  </Section>
                </div>
              </div>
            )} */}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ViewCmsPage;
